/**
 * Auth API for the password-recovery flow (DEV-3110 ⇄ backend DEV-3116).
 *
 * Both endpoints return `BaseRestPayload` with already-localized strings (no
 * machine code), surfaced to the user verbatim. `unavailable` flags a transport
 * failure (network / 5xx) so the UI can show a generic message instead.
 */

/** Result surfaced to the screens. `message` is the server's localized copy. */
export interface AuthApiResult {
  success: boolean;
  message?: string;
  /** True when the call never reached a business response (network / 5xx). */
  unavailable?: boolean;
}

interface BaseRestPayload {
  success: boolean;
  error?: string;
  message?: string;
}

async function postRest(path: string, body: unknown): Promise<AuthApiResult> {
  try {
    const res = await fetch(path, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    // Business failures return HTTP 200 with success:false; a non-OK status is infra.
    if (!res.ok) return { success: false, unavailable: true };
    const json = (await res.json()) as BaseRestPayload;
    return { success: json.success, message: json.message ?? json.error };
  } catch {
    return { success: false, unavailable: true };
  }
}

/** Request a password-reset link for an email address. */
export function requestPasswordRestore(email: string): Promise<AuthApiResult> {
  return postRest('/api/requestRestore', { email });
}

/** Set a new password using a reset token. */
export function restorePassword(newPassword: string, token: string): Promise<AuthApiResult> {
  return postRest('/api/restorePassword', { newPassword, token });
}
